<script context="module" lang="ts">
  //@ts-ignore
  const mapsKey: string = window.Xert?._mapsKey ?? '';

  /**
   * One shared load of the Maps API for the whole page, deferred until a picker is first opened.
   * Module scope on purpose: two of these dialogs are mounted at once -- the filter sidebar and the
   * mobile filter sheet each render their own -- and a per-instance loader would inject the script
   * twice, which Google's own loader warns about. Deferred because most athletes never open the
   * picker at all, and the Maps bundle isn't small. (LibLoader can express neither.)
   *
   * `places` is what backs the search dropdown; `marker` is what draws the pin.
   */
  let mapsPromise: Promise<void> | null = null;

  function loadMaps(): Promise<void> {
    if ((window as any).google?.maps?.places) return Promise.resolve();
    if (mapsPromise) return mapsPromise;
    mapsPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${mapsKey}&libraries=marker,places&region=CA`;
      script.async = true;
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', () => {
        // Cleared so the next open gets a fresh attempt rather than the already-failed promise.
        mapsPromise = null;
        reject(new Error('Google Maps failed to load'));
      });
      document.head.appendChild(script);
    });
    return mapsPromise;
  }

  /**
   * Both Places and Geocoding are separate products from the Static Maps API the rest of the site
   * uses, and each has to be switched on for the Cloud project in its own right. When one isn't,
   * the call comes back REQUEST_DENIED -- worth saying out loud rather than showing an empty
   * dropdown, because it looks exactly like "no such place" from the outside.
   */
  const DENIED_MESSAGE =
    "Location search isn't available: the Places API isn't enabled for this site's Google Maps key. " +
    'You can still click the map to drop a pin.';

  /**
   * Where the athlete is, asked for once per page and remembered -- the picker opens on it when the
   * filter has no pin yet, and both of these dialogs are mounted at once, so a per-instance lookup
   * would put the browser's permission prompt up twice. A refusal, a timeout or a browser without
   * geolocation all resolve to null and leave the map where it was.
   */
  let herePromise: Promise<{ lat: number; lng: number } | null> | null = null;

  function locateHere(): Promise<{ lat: number; lng: number } | null> {
    if (herePromise) return herePromise;
    herePromise = new Promise((resolve) => {
      if (!('geolocation' in navigator)) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
        () => resolve(null),
        // A rough fix is plenty for centring a map, and a fresh one isn't worth the wait.
        { timeout: 10000, maximumAge: 5 * 60 * 1000, enableHighAccuracy: false }
      );
    });
    return herePromise;
  }
</script>

<script lang="ts">
  // Choosing a place for the "starts near" / "goes through" filters. Searching and clicking the map
  // are the same act here -- both drop the pin -- and the circle around it is the filter's radius
  // drawn to scale, so how much ground the filter covers is visible before it's applied rather
  // than inferred from a number in a dropdown.
  import { tick } from 'svelte';
  import * as Dialog from '$lib/components/ui/dialog';
  import { Button } from '@/lib/components/ui/button';
  import { Input } from '@/lib/components/ui/input';
  import { cn } from '@/lib/utils';
  import { activityGpsRadiusOptions, type PickedLocation } from './ActivityFilterFields';

  export let open: boolean = false;
  export let title: string = 'Choose a location';
  export let hint: string = '';
  /** Where the filter already points, so reopening the picker starts where it left off. */
  export let point: PickedLocation | null = null;
  /** The radius the filter is currently set to, in km. Seeds the picker's own radius on open. */
  export let radiusKm: number = 5;
  /** Both the place and the radius are settled here, so they're applied together on confirm. */
  export let onConfirm: (picked: PickedLocation, radiusKm: number) => void = () => {};

  // Same map style the activity map uses, so a picked location looks like the rides drawn on it.
  const MAP_ID = '7b6aa0bf344b0793';

  let mapElement: HTMLElement | null = null;
  let map: any = null;
  let marker: any = null;
  let circle: any = null;

  let autocomplete: any = null;
  let places: any = null;
  let geocoder: any = null;
  /** Ties the keystrokes and the eventual details lookup into one billable Places session. */
  let sessionToken: any = null;

  type Suggestion = { placeId: string; primary: string; secondary: string };
  let suggestions: Suggestion[] = [];
  let highlighted = -1;
  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  // The radius the circle is drawn at while the picker is open. Kept separate from the `radiusKm`
  // prop so backing out with Cancel leaves the filter's radius alone, the same as the pin does.
  let selectedRadius = radiusKm;

  let ready = false;
  let search = '';
  let busy = false;
  /** Waiting on the browser's fix, with nothing on the map yet -- see startHere(). */
  let locating = false;
  let error = '';
  let picked: PickedLocation | null = null;

  // The dialog only puts its content in the DOM while it's open, so the map is built on each open
  // and dropped on each close rather than being kept alive behind a hidden panel.
  $: if (open) void buildMap();
  $: if (!open) teardown();
  // Reopening starts from whatever the filter is set to now.
  $: if (open) selectedRadius = radiusKm;

  const maps = () => (window as any).google?.maps;

  async function buildMap() {
    if (map) return;

    picked = point;
    error = '';
    search = '';
    suggestions = [];

    try {
      await loadMaps();
    } catch (e) {
      error = "Couldn't load Google Maps. Check your connection and try again.";
      return;
    }
    ready = true;

    // The dialog's content mounts a tick after `open` flips, so wait for the element to exist --
    // and bail if the picker was closed again while the script was still coming down.
    await tick();
    if (!mapElement || !open) return;

    const g = maps();
    map = new g.Map(mapElement, {
      // With nothing chosen yet, start far enough out that anywhere is one search away -- startHere()
      // pulls it in to the athlete once the browser says where that is.
      center: point ? { lat: point.lat, lng: point.lng } : { lat: 20, lng: 0 },
      zoom: point ? 11 : 2,
      mapId: MAP_ID,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      clickableIcons: false,
    });

    map.addListener('click', (event: any) => {
      if (!event?.latLng) return;
      void pick(event.latLng.lat(), event.latLng.lng(), null);
    });

    autocomplete = new g.places.AutocompleteService();
    places = new g.places.PlacesService(map);
    geocoder = new g.Geocoder();
    sessionToken = new g.places.AutocompleteSessionToken();

    if (point) {
      draw(point.lat, point.lng);
    } else {
      void startHere();
    }
  }

  /**
   * Open on the athlete's own position when the filter has no pin yet: rides start from home far
   * more often than anywhere else, so that is the pin most of them want, and the ones who don't are
   * a click or a search away from moving it. The map is usable the whole time the fix is coming, so
   * anything done in the meantime wins -- a pin dropped by hand, a place searched for, or the picker
   * closed again.
   */
  async function startHere() {
    locating = true;
    const here = await locateHere();
    locating = false;
    if (!here || !map || !open || picked) return;
    await pick(here.lat, here.lng, null);
    if (circle) map.fitBounds(circle.getBounds());
  }

  function teardown() {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = null;
    suggestions = [];
    highlighted = -1;
    autocomplete = null;
    places = null;
    geocoder = null;
    sessionToken = null;
    marker = null;
    circle = null;
    map = null;
    ready = false;
    busy = false;
    locating = false;
  }

  /** Put (or move) the pin and its radius circle. Purely visual -- `picked` is set by pick(). */
  function draw(lat: number, lng: number) {
    const g = maps();
    if (!g || !map) return;
    const position = { lat, lng };

    if (!marker) {
      // AdvancedMarkerElement needs the `marker` library, which is what loadMaps() asks for -- but
      // another page may have pulled Maps in without it, and the classic marker draws the same pin.
      const Advanced = g.marker?.AdvancedMarkerElement;
      marker = Advanced ? new Advanced({ map, position }) : new g.Marker({ map, position });
    } else if (marker.setPosition) {
      marker.setPosition(position);
    } else {
      marker.position = position;
    }

    if (!circle) {
      circle = new g.Circle({
        map,
        center: position,
        radius: selectedRadius * 1000,
        strokeColor: '#0F9BAF',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#0F9BAF',
        fillOpacity: 0.12,
        clickable: false,
      });
    } else {
      circle.setCenter(position);
      circle.setRadius(selectedRadius * 1000);
    }
  }

  const coordsLabel = (lat: number, lng: number) => `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

  /**
   * Settle on a point. A place chosen from the dropdown arrives with its own name; a point clicked
   * on the map gets one looked up, and keeps its coordinates as the label if the geocoder has
   * nothing to say (or isn't enabled) -- the coordinates are what the filter uses either way.
   */
  async function pick(lat: number, lng: number, label: string | null) {
    error = '';
    suggestions = [];
    picked = { lat, lng, label: label ?? coordsLabel(lat, lng) };
    draw(lat, lng);
    if (label || !geocoder) return;

    geocoder.geocode({ location: { lat, lng } }, (results: any[], status: string) => {
      // Only adopt the name if this is still the point being asked about -- a second click while
      // the first lookup was in flight would otherwise relabel the newer pin with the older name.
      if (status !== 'OK' || !results?.[0] || picked?.lat !== lat || picked?.lng !== lng) return;
      picked = { lat, lng, label: results[0].formatted_address };
    });
  }

  /** Ask Places for matches as the athlete types, a short pause after they stop. */
  function onSearchInput() {
    if (searchTimer) clearTimeout(searchTimer);
    highlighted = -1;
    const input = search.trim();
    if (input.length < 3 || !autocomplete) {
      suggestions = [];
      return;
    }
    searchTimer = setTimeout(() => {
      autocomplete.getPlacePredictions({ input, sessionToken }, (predictions: any[], status: string) => {
        if (status === 'ZERO_RESULTS') {
          suggestions = [];
          return;
        }
        if (status !== 'OK') {
          suggestions = [];
          if (status === 'REQUEST_DENIED') error = DENIED_MESSAGE;
          return;
        }
        error = '';
        suggestions = (predictions ?? []).slice(0, 6).map((prediction) => ({
          placeId: prediction.place_id,
          primary: prediction.structured_formatting?.main_text ?? prediction.description,
          secondary: prediction.structured_formatting?.secondary_text ?? '',
        }));
      });
    }, 250);
  }

  /** Resolve a chosen suggestion to a point, and frame the circle so its size reads immediately. */
  function choose(suggestion: Suggestion) {
    if (!places) return;
    busy = true;
    search = suggestion.primary;
    suggestions = [];
    places.getDetails(
      { placeId: suggestion.placeId, fields: ['geometry', 'formatted_address', 'name'], sessionToken },
      (place: any, status: string) => {
        busy = false;
        // A session ends with the details call it paid for; the next search starts a new one.
        // Held in a local first: esbuild mis-parenthesises `new (maps().places.X)()` into
        // `new (maps()).places.X()`, which calls maps itself as a constructor.
        const g = maps();
        sessionToken = new g.places.AutocompleteSessionToken();
        if (status !== 'OK' || !place?.geometry?.location) {
          error = status === 'REQUEST_DENIED' ? DENIED_MESSAGE : "Couldn't look that place up.";
          return;
        }
        const location = place.geometry.location;
        void pick(location.lat(), location.lng(), place.formatted_address ?? place.name ?? suggestion.primary);
        if (map && circle) map.fitBounds(circle.getBounds());
      }
    );
  }

  function onSearchKeydown(event: KeyboardEvent) {
    if (!suggestions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      highlighted = (highlighted + 1) % suggestions.length;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      highlighted = (highlighted - 1 + suggestions.length) % suggestions.length;
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(suggestions[highlighted >= 0 ? highlighted : 0]);
    } else if (event.key === 'Escape') {
      suggestions = [];
    }
  }

  /** Resize the circle in place, and reframe it so the new coverage reads at a glance. */
  function chooseRadius(km: number) {
    selectedRadius = km;
    if (!circle) return;
    circle.setRadius(km * 1000);
    if (map) map.fitBounds(circle.getBounds());
  }

  function confirm() {
    if (!picked) return;
    onConfirm(picked, selectedRadius);
    open = false;
  }
</script>

<Dialog.Root bind:open portal="#shadcn-ui-dialog-target">
  <Dialog.Content class="flex flex-col max-h-[95dvh] max-w-[640px] w-[95vw] overflow-visible rounded-xl">
    <Dialog.Header>
      <Dialog.Title>{title}</Dialog.Title>
      {#if hint}
        <Dialog.Description>{hint}</Dialog.Description>
      {/if}
    </Dialog.Header>

    <!-- The suggestions hang below the input rather than pushing the map down. -->
    <div class="relative">
      <Input
        placeholder="Search for a place or address..."
        autocomplete="off"
        bind:value={search}
        on:input={onSearchInput}
        on:keydown={onSearchKeydown}
        disabled={!ready}
      />
      {#if suggestions.length}
        <ul class="absolute z-50 top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto list-none m-0 p-1 rounded-md border border-solid border-border bg-background shadow-lg">
          {#each suggestions as suggestion, index}
            <li>
              <button
                type="button"
                class={cn(
                  'w-full text-left px-2 py-1.5 rounded-sm bg-transparent border-0 cursor-pointer hover:bg-accent',
                  index === highlighted ? 'bg-accent' : ''
                )}
                on:mouseenter={() => (highlighted = index)}
                on:click={() => choose(suggestion)}
              >
                <div class="text-sm truncate">{suggestion.primary}</div>
                {#if suggestion.secondary}
                  <div class="text-xs text-muted-foreground truncate">{suggestion.secondary}</div>
                {/if}
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>

    {#if error}
      <p class="text-xs text-red-500 m-0">{error}</p>
    {/if}

    <div class="relative w-full h-[340px] rounded-md overflow-hidden border border-solid border-border bg-muted/40">
      <!-- [&_img]:max-w-none: the app's global image rule squashes the map's own tiles otherwise. -->
      <div bind:this={mapElement} class="absolute inset-0 [&_img]:max-w-none"></div>
      {#if !ready}
        <div class="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          Loading map...
        </div>
      {/if}
    </div>

    <div class="flex flex-row flex-wrap items-center gap-1.5">
      <span class="text-xs text-muted-foreground mr-1">Within</span>
      {#each activityGpsRadiusOptions as option}
        <Button
          variant={option.value === selectedRadius ? 'default' : 'outline'}
          size="sm"
          class="rounded-full text-xs h-7 px-3"
          on:click={() => chooseRadius(option.value)}
        >
          {option.label}
        </Button>
      {/each}
    </div>

    <p class="text-xs text-muted-foreground m-0 truncate">
      {#if busy}
        Looking that place up...
      {:else if picked}
        <i class="fa-solid fa-location-dot mr-1" aria-hidden="true"></i>{picked.label}
      {:else if locating}
        Finding your location...
      {:else}
        Click the map to drop a pin, or search for a place.
      {/if}
    </p>

    <Dialog.Footer class="gap-2 flex-row justify-end">
      <Button variant="outline" on:click={() => (open = false)}>Cancel</Button>
      <Button on:click={confirm} disabled={!picked}>Use this location</Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
